import type { UseLocalStorageKeysKeys } from "@dealer-center/core-interfaces-components";
import { getLocalStorageKeyValue } from "@dealer-center/core-interfaces-components";
import { useState } from "react";

/**
 * Storage type options: localStorage or sessionStorage
 */
export type StorageType = "local" | "session";

/**
 * Hook for easy interaction with browser storage (localStorage or sessionStorage)
 *
 * This hook provides a stateful value that is synchronized with browser storage
 * and behaves like useState. When the state changes, it automatically updates
 * the value in storage.
 *
 * @param key - The key to use in storage (from predefined keys)
 * @param initialValue - Default value if not found in storage
 * @param storageType - Which storage to use: 'local' (default) or 'session'
 * @returns A tuple containing the current value and a setter function
 *
 * @example
 * // Basic usage
 * const [favorites, setFavorites] = useLocalStorageHook(key.ddcAccountFavorites, []);
 *
 * // With sessionStorage
 * const [userSettings, setUserSettings] = useLocalStorageHook(
 *   key.userSettings,
 *   { theme: 'light' },
 *   'session'
 * );
 */
export const useLocalStorageHook = <TValueType>(
  key: UseLocalStorageKeysKeys,
  initialValue: TValueType,
  storageType: StorageType = "local"
): [
  TValueType,
  (value: TValueType | ((val: TValueType) => TValueType)) => void
] => {
  // Get the appropriate storage object
  const getStorage = () =>
    storageType === "local" ? window.localStorage : window.sessionStorage;

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<TValueType>(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }

    try {
      // Get from storage by key
      const storageKey = getLocalStorageKeyValue(key);
      const item = getStorage().getItem(storageKey);

      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.warn(`Error reading from ${storageType}Storage:`, error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to storage.
  const setValue = (
    value: TValueType | ((val: TValueType) => TValueType)
  ): void => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;

      // Save state
      setStoredValue(valueToStore);

      // Save to storage
      if (typeof window !== "undefined") {
        const storageKey = getLocalStorageKeyValue(key);
        getStorage().setItem(storageKey, JSON.stringify(valueToStore));
      }
    } catch (error) {
      console.error(`Error writing to ${storageType}Storage:`, error);
    }
  };

  return [storedValue, setValue];
};
